import { useState } from 'react'
import { toast } from 'react-toastify'
import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { useAccount } from 'wagmi'

import { TokenSmartContractInteraction } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/utilities/tokenContractInteraction'

import { useAuthState } from 'src/contexts/authContext'
import { evmNetworksData, evmNetworksDataType } from 'src/util/networksData'

export interface IBurnEvmToken {
  type: 'burn'
  txId?: string
  address?: string
  tokenTicker: string
  supply: number
  smartContractAddress?: string
  network: string
  chain?: string
}

export interface IMintEvmToken {
  type: 'mint'
  txId?: string
  address?: string
  tokenTicker: string
  supply: number
  smartContractAddress?: string
  network: string
  chain?: string
}

export interface IApproveEvmToken {
  type: 'approve'
  txId?: string
  address?: string
  supply: number
  smartContractAddress?: string
  network: string
  chain?: string
  tokenScAddress?: string
}

export interface IUpdateEvmToken {
  type: 'update'
  txId?: string
  address?: string
  tokenTicker: string
  canBurn: boolean
  canMint: boolean
  network: string
  chain?: string
}

const ethereum = window.ethereum

const useEvmToken = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isConnected, address } = useAccount()

  const user = useAuthState().user

  let zoid = new ZoidCore({
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  })

  const handleToken = async (
    data: IBurnEvmToken | IMintEvmToken | IUpdateEvmToken | IApproveEvmToken,
  ) => {
    setIsLoading(true)
    const networkData =
      evmNetworksData[data.chain?.toUpperCase() as keyof evmNetworksDataType][data.network]
    const chainId = data.network === 'mainnet' ? 1 : 11155111
    let smartContractAddress = '' as string

    if (data.type === 'approve') {
      smartContractAddress = data.tokenScAddress as string
    } else if (data.type === 'mint' || data.type === 'burn') {
      smartContractAddress = data.smartContractAddress as string
    }

    const payload = {
      address,
      fromAddress: address,
      smartContractAddress: smartContractAddress,
      parameters:
        data.type === 'update'
          ? {
              canMint: data.canMint,
              canBurn: data.canBurn,
            }
          : {
              account: data.smartContractAddress,
              amount: data.supply,
            },
      contractFunction: data.type,
      networkProvider: networkData.rpcUrls[0],
      environment: data.network,
      ...(data.chain === 'ETHEREUM' ? { chainId: chainId } : {}),
    }
    try {
      let response
      switch (data.chain) {
        case 'ETHEREUM':
          response = await zoid.ethereum.ethTokenContractInteract(
            payload as TokenSmartContractInteraction,
          )
          break
        case 'BNB':
          response = await zoid.bsc.bscTokenContractInteract(
            payload as TokenSmartContractInteraction,
          )
          break
        case 'POLYGON':
          response = await zoid.polygon.maticTokenContractInteract(
            payload as TokenSmartContractInteraction,
          )
          break
      }
      const params = response.data?.data

      await ethereum?.request({
        method: 'eth_sendTransaction',
        params: [params],
      })
    } catch (err) {
      setIsLoading(false)
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  return { isLoading, isWalletConnected: isConnected, handleToken }
}

export default useEvmToken
